import '../node_modules/bootstrap/dist/css/bootstrap.min.css';

import {Route, Routes, Navigate, useLocation} from 'react-router-dom';
import { jwtDecode } from "jwt-decode";

import Login from './pages/auth/Login';
import MultiFactorAuthentication  from './pages/auth/MultiFactorAuthentication';
import RecoverPassword from './pages/auth/RecoverPassword';
import RegistrationStage1 from './pages/auth/RegistrationStage1';
import RegistrationStage2 from './pages/auth/RegistrationStage2';
import RegistrationStage3 from './pages/auth/RegistrationStage3';
import Settings from './pages/auth/Settings';
import CorporateSettings from './pages/auth/CorporateSettings';

import Talents from './pages/userPages/Talents';
import FaqPage from './pages/userPages/FaqPage';
import OpenPositions from './pages/userPages/OpenPositions';
import NewOpenPosition from './pages/userPages/NewOpenPosition';
import TeamAnalytics from './pages/userPages/TeamAnalytics';
import PoolAnalytics from './pages/userPages/PoolAnalytics';
import TeamReview from './pages/userPages/TeamReview';
import ErrorPage from './pages/errorPage/ErrorPage';
import Onboarding from './pages/userPages/Onboarding';
import SetPassword from './pages/userPages/SetPassword';
import TeamSimulatorPage from './pages/userPages/TeamSimulatorPage';
import Sidebar from './components/layoutComponents/navigation/Sidebar';
import MobileNav from './components/layoutComponents/navigation/MobileNav';
import * as Constants from './constants';
import {useSelector} from 'react-redux';
import { companiesWithUserRolesManagement } from './content';
import {useEffect, useMemo} from "react";

import { ThemeProvider } from '@mui/material';
import CacheBuster from 'react-cache-buster';
import packageInfo from '../package.json';

import 'react-toastify/dist/ReactToastify.css';
import B2BApiService from "./B2bApiService";
import { MUITheme } from './MUITheme';

import { logPageView } from './analytics';
import {
    detectLocale,
    getLocaleFromPathname,
    isRestrictedAuthPath,
    stripLocale,
    withLocale,
    useLocale,
} from './locale';
import PublicLocaleGate from './locale/PublicLocaleGate';
import LegacyPublicRedirect from './locale/LegacyPublicRedirect';
import SupportChat from './components/supportChat/SupportChat';

function App() {
    const location = useLocation();
    const { locale, contentRevision, contentSource } = useLocale();
    const companyData = useSelector(state => state.company.info);
    const companyIsActive = useSelector(state => state.company.isActive);
    const authenticationData = JSON.parse(localStorage.getItem(Constants.MAZHR_JWT_KEY)) || null;
    const userRole = JSON.parse(localStorage.getItem(Constants.LOCAL_STORAGE_KEY_USER_ROLE_ID)) || authenticationData?.role_id || null;
    let isAuthenticated = false;
    let isMFARequired = false;

    const isProduction = process.env.NODE_ENV === 'production';
    const pathLocale = getLocaleFromPathname(location.pathname);
    const strippedPath = stripLocale(location.pathname);

    let iam_enabled_companies = companiesWithUserRolesManagement || Constants.IAM_ENABLED_COMPANIES || [];

    const b2bServiceObj = useMemo(() => {
        return new B2BApiService();
    }, []);

    useEffect(() => {
        logPageView();
    }, []);

    useEffect(() => {
        if (isAuthenticated && !companyData.name) {
            b2bServiceObj.LoadCompanyData();
        }
        // eslint-disable-next-line
    }, [isAuthenticated, companyData.name, b2bServiceObj]);

    if (authenticationData && authenticationData?.access_token) {
        const decodedJWT = jwtDecode(authenticationData?.access_token?.toString());
        let company_enforce_mfa, mfa_activated, mfa_verified;
        // For integrity check, if JWT is decoded, use the values from JWT
        if (decodedJWT) {
            company_enforce_mfa = decodedJWT.company_enforce_mfa;
            mfa_activated = decodedJWT.mfa_activated;
            mfa_verified = decodedJWT.mfa_verified;
        } else { // Otherwise, use the values from local storage
            company_enforce_mfa = authenticationData.company_enforce_mfa;
            mfa_activated = authenticationData.mfa_activated;
            mfa_verified = authenticationData.mfa_verified;
        }
        
        /* Case: MFA is not activated and verified 
         * Case: company does not enforce MFA and MFA is not activated
         * => no MFA required + access_token available => authenticated
         */ 
        if ((mfa_activated && mfa_verified) || (!company_enforce_mfa && !mfa_activated)) {
            isMFARequired = false;
            isAuthenticated = true;
        } else {
            /* Other cases
            * - Company enforces MFA and MFA is not activated
            * - Company enforces MFA and MFA is activated but not verified
            * - Company doesnot enforce MFA and MFA is activated but not verified
            * => MFA required + access_token available => not authenticated 
            */ 
            isMFARequired = true;
            isAuthenticated = false;
        }
    }

    // Redirect authenticated users away from public auth paths (locale-aware)
    if (isAuthenticated && isRestrictedAuthPath(location.pathname)) {
        return <Navigate to="/dashboard" replace/>;
    }

    const showAppNav = isAuthenticated
        && strippedPath !== "/recover-password"
        && strippedPath !== "/terms-conditions"
        && !pathLocale;

    const mfaRedirectTo = withLocale(detectLocale(), '/mfa');

    return (
        <CacheBuster
            currentVersion={packageInfo.version}
            isEnabled={isProduction} //If false, the library is disabled.
            isVerboseMode={false} //If true, the library writes verbose logs to console.
        >
            <ThemeProvider theme={MUITheme}>
                <div className={`App locale-${locale}`} data-content-revision={contentRevision} data-content-source={contentSource}>
                    <div className="page">
                        {showAppNav && (
                            window.innerWidth > 992 ?
                                <Sidebar userRole={userRole}/>
                                :
                                <MobileNav userRole={userRole}/>
                        )}
                        {showAppNav && (
                            <SupportChat
                                locale={locale}
                                route={strippedPath || '/'}
                                roleId={userRole}
                                companyActive={companyIsActive}
                                accessToken={authenticationData?.access_token}
                            />
                        )}
                        <Routes>
                            {isAuthenticated && companyIsActive && <Route path="/" element={<Onboarding userRole={userRole}/>}/>}

                            {isAuthenticated && companyIsActive && <Route path="/dashboard" element={<Onboarding userRole={userRole}/>}/>}

                            {isAuthenticated && companyIsActive && userRole && ![Constants.USER_ROLE_OWNER].includes(userRole) && <Route path="open-positions" element={<OpenPositions/>}/>}

                            {isAuthenticated && companyIsActive && userRole && ![Constants.USER_ROLE_OWNER].includes(userRole) && <Route path="open-positions/:hash" element={<OpenPositions/>}/>}

                            {isAuthenticated && companyIsActive && userRole && ![Constants.USER_ROLE_OWNER].includes(userRole) && <Route path="new-open-position" element={<NewOpenPosition/>}/>}

                            {isAuthenticated && companyIsActive &&  userRole && ![Constants.USER_ROLE_OWNER].includes(userRole) && <Route path="talents" element={<Talents userRole={userRole}/>}/>}

                            {isAuthenticated && companyIsActive && userRole && ![Constants.USER_ROLE_OWNER].includes(userRole) && <Route path="team-simulator" element={<TeamSimulatorPage/>}/>}

                            {isAuthenticated && companyIsActive && userRole && ![Constants.USER_ROLE_OWNER].includes(userRole) && <Route path="team-analytics" element={<TeamAnalytics/>}/>}

                            {isAuthenticated && companyIsActive && userRole && ![Constants.USER_ROLE_OWNER].includes(userRole) && <Route path="candidate-analytics" element={<PoolAnalytics/>}/>}

                            {isAuthenticated && companyIsActive &&  userRole && ![Constants.USER_ROLE_OWNER].includes(userRole) && <Route path="team-review" element={<TeamReview/>}/>}

                            {isAuthenticated && companyIsActive &&  userRole && ![Constants.USER_ROLE_OWNER].includes(userRole) && <Route path="team-review/:hash" element={<TeamReview/>}/>}

                            {isAuthenticated && companyIsActive &&  userRole && ![Constants.USER_ROLE_OWNER].includes(userRole) && <Route path="talents/:hashParam" element={<Talents userRole={userRole}/>}/>}

                            {isAuthenticated && <Route path="faq-page" element={<FaqPage />} />}
                            {isAuthenticated && <Route path="settings" element={<Settings />}/>}
                            {isAuthenticated && [...Constants.ADMINISTRATIVE_USER_ROLES, Constants.USER_ROLE_HRD].includes(userRole)
                                && <Route path="corporate-settings" element={
                                    <CorporateSettings
                                        userRole={userRole}
                                        userEmail={authenticationData?.email}
                                        companyId={authenticationData?.company_id}
                                        iamEnabled={iam_enabled_companies.includes(authenticationData?.company_id)
                                            && [...Constants.ADMINISTRATIVE_USER_ROLES, Constants.USER_ROLE_HRD].includes(userRole)}
                                    />}/>}
                            {isAuthenticated && <Route path="*" element={<Navigate to="/dashboard" />}/>}

                            {/* Legacy public paths → /:locale/... */}
                            <Route path="login" element={<LegacyPublicRedirect toPath="/login" />}/>
                            <Route path="register" element={<LegacyPublicRedirect toPath="/register" />}/>
                            <Route path="register-2" element={<LegacyPublicRedirect toPath="/register-2" />}/>
                            <Route path="register-3" element={<LegacyPublicRedirect toPath="/register-3" />}/>
                            <Route path="set-password" element={<LegacyPublicRedirect toPath="/set-password" />}/>
                            <Route path="recover-password" element={<LegacyPublicRedirect toPath="/recover-password" />}/>
                            <Route path="mfa" element={<LegacyPublicRedirect toPath="/mfa" />}/>

                            {/* Locale-prefixed public auth routes */}
                            <Route path=":locale" element={<PublicLocaleGate />}>
                                {authenticationData?.access_token && isMFARequired ? (
                                    <>
                                        <Route path="mfa" element={<MultiFactorAuthentication userRole={userRole} />}/>
                                        <Route path="set-password" element={<SetPassword/>}/>
                                        <Route path="recover-password" element={<RecoverPassword/>}/>
                                        <Route path="*" element={<Navigate to={mfaRedirectTo} replace />}/>
                                    </>
                                ) : (
                                    <>
                                        <Route path="login" element={<Login/>}/>
                                        <Route path="register" element={<RegistrationStage1/>}/>
                                        <Route path="register-2" element={<RegistrationStage2/>}/>
                                        <Route path="register-3" element={<RegistrationStage3/>}/>
                                        <Route path="set-password" element={<SetPassword/>}/>
                                        <Route path="recover-password" element={<RecoverPassword/>}/>
                                        <Route index element={<LegacyPublicRedirect toPath="/login" />}/>
                                        <Route path="*" element={<ErrorPage/>}/>
                                    </>
                                )}
                            </Route>

                            {authenticationData?.access_token && isMFARequired ? (
                                <Route path="*" element={<Navigate to={mfaRedirectTo} replace />}/>
                            ) : (
                                <>
                                    {!isAuthenticated && (
                                        <Route path="/" element={<LegacyPublicRedirect toPath="/login" />}/>
                                    )}
                                    {!isAuthenticated && (
                                        <Route path="*" element={<ErrorPage/>}/>
                                    )}
                                </>
                            )}
                        </Routes>
                    </div>
                </div>
            </ThemeProvider>
        </CacheBuster>
    );
}

export default App;
